<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref, watch } from "vue";
import { ProductGroup, SearchResultRecommendList } from "../../services/search.service";
import ProductTeaser from "../../components/productTeaser/productTeaser.vue";
import ProductsModuleContentSkeleton from "./productsModuleContentSkeleton.vue";
import { useFavoritesStore } from "../../stores/favoritesStore";
import GtmService from "../../services/gtm.service";
import { useLazy } from "../../composables/useLazy";
import $ from "jquery";
import { HotjarTrackingService } from "../../services/hotjarTracking.service";

const props = withDefaults(
  defineProps<{
    heading: string;
    userAuthenticated: boolean;
    searchResultRecommendList?: SearchResultRecommendList;
    isLoading?: boolean;
    isLoadingFavorites?: boolean;
    showAsColumnOnMobile?: boolean;
    useSliderForDesktop?: boolean;
    containerClass?: string;
    smallLabel?: boolean;
    hideSizes?: boolean;
    hideColors?: boolean;
    hidePromotion?: boolean;
    productCount?: number;
    useSkeletonLoaders?: boolean;
    hideFavorites?: boolean;
    itemListName?: string;
    itemListId?: string;
    upsellAlgorithm?: string;
    trackViewItemList?: boolean;
    trackSelectItem?: boolean;
    isQuickAdd?: boolean;
    teaserProductSize?: string;
    teaserProductCount?: number;
  }>(),
  {
    teaserProductSize: "small", 
    teaserProductCount: 4,
  }
);


const favoritesStore = useFavoritesStore();
const id = `${props.itemListId}-content`;
const sliderRef = ref<HTMLElement | null>(null);
const parentRef = ref<HTMLElement | null>(null);
let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

// Function to initialize the slider
const initializeSlider = () => {
  if (!sliderRef.value) return;

  const hasItems = $(sliderRef.value).find("li").length > 0;
  if (!hasItems) return; // Prevent initializing Slick on an empty list

  if (props.useSliderForDesktop && window.matchMedia("(min-width: 47.9375em)").matches) {
    $(sliderRef.value).not(".slick-initialized").slick({
      slidesToShow: props.teaserProductCount,
      slidesToScroll: 1,
      arrows: true,
      infinite: false,
    });
    if (parentRef.value) {
        parentRef.value.classList.add('has-slick-initialized');
    }
  }
};

// Function to destroy the slider
const destroySlider = () => {
  if ($(sliderRef.value).hasClass("slick-initialized")) {
    $(sliderRef.value).slick("unslick");
    if (parentRef.value) {
      parentRef.value.classList.remove('has-slick-initialized');
    }
  }
};

const handleIntersection = () => {
  if (props.trackViewItemList && props.searchResultRecommendList?.primaryList?.productGroups) {
    GtmService.pushViewItemListEvent(
      props.itemListId ?? "",
      props.itemListName ?? "",
      props.userAuthenticated,
      props.searchResultRecommendList,
      undefined,
      props.upsellAlgorithm,
    );
    HotjarTrackingService.trackStateChange(props.itemListName?.includes("cartstatus") ? "add-to-cart-popup" : undefined);
  }
};

onBeforeMount(() => {
  useLazy(handleIntersection, `#${id}`, true);
});

onMounted(() => {

  initializeSlider();
  // Listen for resize events
  window.addEventListener("resize", () => {
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      destroySlider();
      initializeSlider();
    }, 250);
  });

  if (props.userAuthenticated) {
    favoritesStore.getFavorites();
  }
});

onUnmounted(() => {
  destroySlider();
  if (resizeTimeout) clearTimeout(resizeTimeout);
});

// Watch for when products are loaded
watch(() => props.searchResultRecommendList, async (newList) => {
  if (newList?.primaryList && newList.primaryList.productGroups && newList.primaryList.productGroups.length > 0) {
    await nextTick(); // Wait for Vue to render the <li> elements
    destroySlider();
    initializeSlider();
  }
}, { deep: true, immediate: true });
</script>

<template>
  <div ref="parentRef" :id="id" v-show="!props.isLoading && searchResultRecommendList?.primaryList?.productGroups?.length" :class="props.containerClass">
    <div class="product-list">
      <!-- Heading -->
      <h2 v-if="props.heading" class="product-list__heading">
        {{ props.heading }}
      </h2>
      
      <!-- Result -->
      <ul
        id="category-list"
        ref="sliderRef"
        :class="[
          'product-list__items',
          `product-list__items--${props.teaserProductSize?.toLowerCase()}`,
          { 'product-list__items--column': props.showAsColumnOnMobile }
        ]"
      >
        <template v-for="(productGroup, index) in searchResultRecommendList?.primaryList?.productGroups" :key="index">
          <template v-if="productGroup.products?.length">
            <li :key="`${productGroup.key}-${productGroup.products[0]?.key}`">
              <ProductTeaser
                :product-group="productGroup"
                :product="productGroup.products[0]"
                :is-user-authenticated="props.userAuthenticated"
                :is-quick-add="props.isQuickAdd"
                :favorites="favoritesStore.favorites"
                :is-loading-products="props.isLoading"
                :is-loading-favorites="favoritesStore.isLoadingFavorites"
                :use-gtm-tracking="true"
                :user-id="''"
                :hide-sizes="props.hideSizes"
                :hide-colors="props.hideColors"
                :hide-promotion="props.hidePromotion"
                :hide-favorites="props.hideFavorites"
                :track-select-item="props.trackSelectItem"
                :item-list-id="props.itemListId"
                :item-list-name="props.itemListName"
                :upsell-algorithm="props.upsellAlgorithm"
                :item-list-index="index"
                @toggleFavorite="favoritesStore.toggleFavorite($event)"
              />
            </li>
          </template>
        </template>
      </ul>
    </div>
  </div>

  <!-- Skeleton Loader -->
  <div
    v-if="props.isLoading && props.useSkeletonLoaders"
    :class="props.containerClass"
    class="product-list product-list__upsell"
  >
    <ProductsModuleContentSkeleton
      :product-count="props.productCount"
      :show-as-column-on-mobile="props.showAsColumnOnMobile"
    />
  </div>
</template>
